
import { LightningElement, api } from 'lwc';


const VARIANTS = ['notify', 'success', 'warning', 'error'];
const MODES = ['pester', 'dismissible', 'sticky'];

/* LWC Checklist
    1. Load on any DB with spinner (full visualization at all times)
    2. 

*/


/*
    Usage (with Record Id): 
        this.toastObj = { 
            variant: 'success', 
            mode: 'sticky', 
            header: { 
                recordId: newOpp.Id, 
                recordName: 'here',
                linkPrefix: 'Lead successfully converted.  View new Opportunity',
                text: '.'
            }        
        };
        
*/



export default class toastLwc extends LightningElement {
    _isClose = false;
    _isCloseMouseOver = false;

    @api autoCloseMs; // optional override; if set, use this regardless of mode
     _timerId;
    _hoverPaused = false;


    @api mode = 'dismissible';


    @api 
    set variant(val) {
        this._variant = val;
        if (val === 'warning') {
            this._applyVarsDirect('--c-icon-color', 'var(--slds-g-color-neutral-base-10, #181818)');
        } else {
            this._applyVarsDirect('--c-icon-color', '#fff');
        }
    }
    get variant() {
        return this._variant ?? 'info';
    }


    @api 
    set topOffset(value) {
        this._topOffset = value;
        if (value) {
            this._applyVarsDirect('--toast-top-offset', value);
        }
    } 
    get topOffset() {
        return this._topOffset;
    }


    _startTimer() {
        if (this._isClose) {
            return;
        }
        this._clearTimer();
        const ms = this._computedTimeout;
        if (!ms) return;

        this._timerId = window.setTimeout(() => {
            if (!this.isConnected) return;

            if (this._hoverPaused || this._isClose || this._isCloseMouseOver) {
                return;
            }

            this._isClose = true;
            this._timerId = null;
        }, ms);
    }

    _clearTimer() {
        if (this._timerId) {
            clearTimeout(this._timerId);
            this._timerId = null;
        }
    }

    _pauseTimer = () => {
        if (this._isClose) {
            return;
        }
        this._hoverPaused = true;
        this._clearTimer();
    };

    _resumeTimer = () => {
        if (this._isClose) {
            return;
        }
        if (!this._hoverPaused) {
            return;
        }
        this._hoverPaused = false;
        this._startTimer();
    };
    
    

    // ================== Main Property ================== \\
    @api objectApiName;  // Need object api name for navigation mixin
    _header;
    // holds generated URL
    _headerUrl;   

    @api
    set header(value) {
        if (value && typeof value === 'object') {
            this._isClose = false;
            this._header = {
                recordId: value.recordId,
                recordName: value.recordName,
                linkPrefix: value.linkPrefix,
                objectApiName: value.objectApiName ?? null,
                text: value.text
            };

            this._buildHeaderUrl();

            if (!this._isClose && !this._isCloseMouseOver) {
                this._hoverPaused = false;
                this._clearTimer();
                //console.log('********** calling _startTimer() in set header() of toast lwc');
                this._startTimer();
            }
        } else {
            this._header = undefined;
            this._headerUrl = undefined;
            this._clearTimer();
        }
    }

    get header() {
        return this._header;
    }

    get headerUrl() {
        return this._headerUrl;
    }
    // =================================================== \\

    _message;
    @api
    set message(v) {
        this._message = v;
        this._isClose = false;
        if (v) {
            this._hoverPaused = false;
            this._clearTimer();
            this._startTimer();
        }
    }
    get message() { return this._message; }

    get isClose() {
        return this._isClose;
    }

    get notifyConfig() {
        const base = 'slds-notify slds-notify_toast';
        const out  = { toastCl: base, iconName: 'utility:info' };
        
        const v = (this.variant || 'notify').toLowerCase();

        if (v === 'success') {
            out.toastCl = `${base} slds-theme_success`;
            out.iconName = 'utility:success';
            out.container = 'slds-icon_container slds-icon-utility-success slds-current-color';
            out.iconUrl = 'static/assets/icons/utility-sprite/svg/symbols.svg#success';

        } else if (v === 'error') {
            out.toastCl = `${base} slds-theme_error`;
            out.iconName = 'utility:error';
            out.container = 'slds-icon_container slds-icon-utility-error slds-current-color';
            out.iconUrl = 'static/assets/icons/utility-sprite/svg/symbols.svg#error';

        } else if (v === 'warning') {
            out.toastCl = `${base} slds-theme_warning`;
            out.container = 'slds-icon_container slds-icon-utility-warning slds-current-color';
            out.iconUrl = 'static/assets/icons/utility-sprite/svg/symbols.svg#warning';

        } else {
            out.toastCl = `${base} slds-theme_info`;
            out.container = 'slds-icon_container slds-icon-utility-info slds-current-color';
            out.iconUrl = 'static/assets/icons/utility-sprite/svg/symbols.svg#info';

        }
        return out;
    }

    handleClose(event) {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        if (this._isClose) {
            // already closed, do nothing
            return;
        }

        this._clearTimer();
        this._hoverPaused = false;
        this._isClose = true;
    }



    _buildHeaderUrl() {
        return;
        if (!this._header?.recordId || (!this.objectApiName && !this._header.objectApiName)) {
            this._headerUrl = undefined;
            return;
        }   
    }

    // NEW: does this toast render a link?
    get hasLink() {
        // anchor shows when header.recordId exists; support header.url too if you ever add it
        return this._headerUrl && this._headerUrl !== undefined;
    }

    // Replace your _computedTimeout getter with this:
    get _computedTimeout() {
        if (Number.isFinite(this.autoCloseMs)) return this.autoCloseMs; // explicit override wins
        const m = (this.mode || 'dismissible').toLowerCase();
        if (m === 'sticky') return 0;            // never auto-close
        if (m === 'pester') return 8000;         // keep your prior default
        // dismissible (and anything else): 4.8s without link, 9.6s with link
        return this.hasLink ? 9600 : 4800;
    }




    // Apply Styles to the host 👉 Can pass 1.Array of Arrays   2.Array of Object   3.Strings
    _applyVarsDirect(cssVar, varStyle=null) {

        // Check that CSS var starts with '--' and has a non-null stlye 
        const isValid = (cssVarP, varStyleP) => {
            return typeof cssVarP === 'string' && 
                typeof varStyleP === 'string' &&
                cssVarP?.startsWith('--') && 
                varStyleP;
        }

        // Get Host elements
        const el = this.template.host; 

        if (Array.isArray(cssVar)) {
            // Handle Array of vars
            cssVar.forEach(hostVar => {
                if (Array.isArray(hostVar) && isValid(hostVar[0], hostVar[1])) {
                    // Handle Array structure  (CSS var at index 0)
                    el.style.setProperty(hostVar[0], hostVar[1]);
                } else if (typeof hostVar === 'object' && isValid(hostVar.cssVar, hostVar.varStyle)) {
                    // Handle Object structure (cssVar & varStyle keys)
                    el.style.setProperty(hostVar.cssVar, hostVar.varStyle)
                }
            });
        } else if (isValid(cssVar, varStyle)) {
            // Handle string arguments
            el.style.setProperty(cssVar,  varStyle);
        }
    }

}
